import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CalendarDays, AlertTriangle, MapPin, Users, Sun } from "lucide-react";
import heroImage from "@/assets/gallery/tubing-canyon-view.jpg";
import inlineImage from "@/assets/gallery/friends-tubing.jpg";

const SLUG = "comal-river-vs-guadalupe-river-comparison";
const CANONICAL = `/blog/${SLUG}`;
const TITLE =
  "Comal River vs. Guadalupe River: Family Float Guide | Son's";
const DESCRIPTION =
  "Compare the Comal River and Guadalupe River in New Braunfels to find the best family float — rules, parking, crowds, water temps, and private cabana alternatives.";
const PUBLISH_DATE = "June 30, 2026";
const HERO_ALT =
  "Cypress-lined Guadalupe River stretch near New Braunfels, an alternative to the busy Comal River float.";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Where can I access or park for the river/float trip?",
    a: "For a public float on the Comal River, you must navigate city parking and city-operated lots near Prince Solms Park, which charge premium rates on peak summer weekends. Alternatively, if you book with Son's Guadalupe on the Guadalupe River, you can park directly onsite on private property, avoiding public lots entirely.",
  },
  {
    q: "What are the current river conditions, hours, or closure thresholds?",
    a: "While the draft body does not list specific hours or closure thresholds, it notes that the Comal River remains a consistent and refreshing 72°F (22°C) year-round because it is 100% spring-fed from Comal Springs, making it highly reliable during hot, dry summers. The Guadalupe River's temperature and flow are variable, as its water source is fed by reservoir runoff from Canyon Lake Dam as well as natural springs.",
  },
  {
    q: "What can I bring, and what items are prohibited on the Comal River?",
    a: 'The City of New Braunfels strictly enforces a "Can Ban" ordinance. Prohibited items include all disposable containers such as cans, plastic bottles, glass, zip-top bags, and paper wrappers. You must use reusable containers like insulated metal tumblers or heavy-duty plastic sports bottles. Additionally, coolers must not exceed 30 quarts in volume, are limited to one per floater, and must have a locking mechanism or strap.',
  },
  {
    q: "Do I need a life jacket or other safety gear?",
    a: "For adults, life jackets are not legally mandated in calm waters, though they are highly recommended for weak swimmers. However, for children under the age of 13, life jackets are legally required. The city provides free life jacket loaner stations at major Comal River access points on a first-come, first-served basis.",
  },
  {
    q: "How long does the float take?",
    a: "The average public float on the short Comal River takes about 2 to 3 hours. On the Guadalupe River, float times are highly flexible and can range from 1 to over 6 hours. If you book through Son's Guadalupe, you can participate in a relaxed, shuttle-assisted float that takes 2 to 3 hours and ends directly back at your private cabana.",
  },
  {
    q: "Which outfitter or resort should I use for tubing or other river activities?",
    a: "For public tubing on the Comal, traditional outfitters include Texas Tubes and Comal Tubes, or The Float In for commercialized riverside seating. On the Guadalupe River, options include Mountain Breeze Campground for a lively, high-energy environment. For a quiet, crowd-free experience with private cabanas, in-water picnic tables, and glamping, Son's Guadalupe on River Road is the premier private alternative.",
  },
  {
    q: "Are kids or pets allowed?",
    a: "Kids are allowed on these river trips, though there are safety considerations to keep in mind. For children under 13, life jackets are legally required. On the Comal River, families with young children often choose to exit before the turbulent Tube Chute to walk around it. For a safer, controlled, and stress-free environment, families often choose the limited-capacity private property at Son's Guadalupe. The draft does not mention pet rules.",
  },
  {
    q: "What does it cost to float, rent equipment, or stay on-site?",
    a: "Specific pricing figures are not provided in the draft body. However, on public Comal River trips, you can expect costs related to public outfitter tube rentals, shuttle services, and premium city parking rates at Prince Solms Park on peak weekends. For a premium, all-in-one cost experience, Son's Guadalupe offers private waterfront cabana rentals with BBQ grills and electricity, in-water covered picnic tables, shuttle-assisted tubing, and overnight glamping in air-conditioned tents.",
  },
];

const FAQS_GUADALUPE: { q: string; a: string }[] = [
  {
    q: "Where can I access or park for the float on the Guadalupe River?",
    a: "On the Guadalupe River, you can avoid city parking entirely. Booking at Son's Guadalupe on River Road in New Braunfels gives you free onsite private parking right next to your cabana — no metered city lots, no shuttle hike from a distant parking garage, and no premium peak-weekend rates.",
  },
  {
    q: "What are the current river conditions, flow, or closure thresholds on the Guadalupe?",
    a: "The Guadalupe River's flow is largely controlled by scheduled releases from Canyon Lake Dam, with additional input from natural springs. Water temperatures run cooler in spring and fall and typically sit in the low to mid-70s during peak summer. Check the USGS Sattler gauge and any TPWD advisories before high-water weekends, since flow can change quickly after dam releases or heavy rain.",
  },
  {
    q: "What can I bring, and what items are prohibited on the Guadalupe River?",
    a: "The City of New Braunfels' \"Can Ban\" does not apply to the Guadalupe stretches outside city limits, including the River Road corridor where Son's Guadalupe sits. That said, Son's enforces its own common-sense rules: no glass, no Styrofoam, reasonable cooler sizes, and a strict leave-no-trace standard so the river stays clean for the next group.",
  },
  {
    q: "Do I need a life jacket or other safety gear on the Guadalupe?",
    a: "Texas law requires a properly fitted U.S. Coast Guard-approved life jacket for every child under 13 on the water. Adults are not legally required to wear one in calm conditions, but life jackets are strongly recommended any time flows are elevated after dam releases or rain. Loaner jackets are typically available at Son's for guests who need them.",
  },
  {
    q: "How long does the float take on the Guadalupe River?",
    a: "It depends on where you put in. A shuttle-assisted float from Son's Guadalupe typically runs about 2 to 3 hours and ends right back at your private cabana. Longer DIY floats from upstream River Road put-ins can stretch to 4 to 6+ hours, especially at lower flows.",
  },
  {
    q: "Which outfitter or resort should I use on the Guadalupe River?",
    a: "For a quiet, family-first day with a private cabana, in-water picnic tables, onsite parking, and a shuttle-assisted float, Son's Guadalupe on River Road is the premier private option. If you prefer a livelier, high-energy public scene, River Road campgrounds like Mountain Breeze offer a more festival-like atmosphere.",
  },
  {
    q: "Are kids or pets allowed on the Guadalupe River?",
    a: "The Guadalupe is a popular family river — kids are welcome with required life jackets (under 13), and the slower stretches near Son's are well-suited to younger floaters. Pet rules vary by property: Son's Guadalupe does not allow pets on tubes or in cabana areas to keep the experience clean and safe for all guests.",
  },
  {
    q: "What does it cost to float, rent equipment, or stay on the Guadalupe?",
    a: "Pricing varies by experience. Son's Guadalupe publishes current rates for tube rentals and shuttle service, private riverside cabanas (with BBQ grills and electricity), in-water covered picnic tables, and overnight glamping in air-conditioned bell tents. There are no city parking fees, and most amenities are bundled into the cabana or glamping rate.",
  },
];

const TOC = [
  { id: "comal-experience", label: "The Comal River Experience" },
  { id: "comparison", label: "Comal vs. Guadalupe (side-by-side)" },
  { id: "outfitters", label: "Public Outfitters Overview" },
  { id: "sons-guadalupe", label: "Son's Guadalupe: Private Alternative" },
  { id: "checklist", label: "Which River Is Right for You?" },
  { id: "faqs-comal", label: "Comal River FAQs" },
  { id: "faqs-guadalupe", label: "Guadalupe River FAQs" },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
      {
        "@type": "ListItem",
        position: 3,
        name: "Comal River vs. Guadalupe River",
        item: `https://sonsguadalupe.com${CANONICAL}`,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Comal River vs. Guadalupe River: Which Float Is Right for Your Family?",
    description:
      "Compare the Comal River and Guadalupe River in New Braunfels to find the best family float for your summer day on the water",
    image: `https://sonsguadalupe.com${heroImage}`,
    author: { "@type": "Organization", name: "Son's Guadalupe" },
    publisher: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      logo: {
        "@type": "ImageObject",
        url: "https://sonsguadalupe.com/og-image.png",
      },
    },
    datePublished: "2026-06-30",
    mainEntityOfPage: `https://sonsguadalupe.com${CANONICAL}`,
    url: `https://sonsguadalupe.com${CANONICAL}`,
    wordCount: 1478,
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [...FAQS, ...FAQS_GUADALUPE].map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

const BlogComalVsGuadalupe = () => {
  const related = getRelatedPosts(SLUG, 3);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        ogImage={heroImage}
        schemaJson={schemaJson}
      />
      <Header />

      <main>
        <article className="container-narrow mx-auto px-4 md:px-8 pt-28 pb-16">
          <BlogBreadcrumb title="Comal River vs. Guadalupe River" />

          <header className="mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-teal font-semibold mb-3">
              Comparison Guide
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-charcoal mb-4">
              Comal River vs. Guadalupe River: Which Float Is Right for Your Family?
            </h1>
            <p className="flex items-center gap-2 text-sm text-charcoal/60 mb-6">
              <CalendarDays className="w-4 h-4" /> Published {PUBLISH_DATE} · 8 min read
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/why-sons">
                <Button variant="hero" size="lg">Why Son's Guadalupe</Button>
              </Link>
              <Link to="/cabanas">
                <Button variant="outline" size="lg">Book a Riverside Cabana</Button>
              </Link>
            </div>
          </header>

          <img
            src={heroImage}
            alt={HERO_ALT}
            width={1600}
            height={900}
            loading="eager"
            decoding="async"
            className="w-full h-auto rounded-2xl shadow-soft mb-10"
          />

          {/* Quick answer */}
          <aside className="rounded-2xl border-l-4 border-teal bg-muted/40 p-6 mb-10">
            <p className="text-xs uppercase tracking-[0.18em] text-teal font-semibold mb-2">
              Quick Answer
            </p>
            <p className="text-charcoal/85 leading-relaxed">
              The <strong>Comal River</strong> is short, urban, and consistently 72°F — great for a
              quick public float with built-in thrills (the Tube Chute), but expect strict container
              rules, paid city parking, and dense weekend crowds. The <strong>Guadalupe River</strong>{" "}
              is longer, more scenic, and flexible — and on private outposts like{" "}
              <Link to="/why-sons" className="text-teal underline font-medium">Son's Guadalupe</Link>{" "}
              you get cabanas, onsite parking, and a shuttle-assisted float that ends at your door.
            </p>
          </aside>

          {/* TOC */}
          <nav aria-label="Table of contents" className="rounded-xl border border-border bg-card/60 p-5 mb-10">
            <p className="font-heading text-sm font-bold text-charcoal mb-3">On this page</p>
            <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6 text-sm">
              {TOC.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="text-teal hover:underline">
                    → {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="prose prose-lg max-w-none text-charcoal/85">
            <p className="text-xl leading-relaxed">
              Planning a day on the water in New Braunfels, Texas, inevitably leads to a classic
              Hill Country debate: should you float the Comal River or the Guadalupe River? Both
              waterways draw thousands of visitors every summer, yet they offer vastly different
              experiences.
            </p>
            <p>
              One is a short, highly-managed urban float famous for its consistent spring-fed
              temperature and intense public crowds. The other winds through cypress-lined limestone
              canyons, with variable flows and opportunities for a quiet, private retreat. This
              guide breaks down the logistics, local rules, safety considerations, and crowd
              dynamics of each — and how a{" "}
              <Link to="/private-vs-public-guadalupe" className="text-teal underline">
                private vs. public Guadalupe
              </Link>{" "}
              setup changes the whole day.
            </p>

            {/* Section 1 */}
            <h2 id="comal-experience" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              The Comal River Experience: Fast Facts, Rules, and Logistics
            </h2>
            <p>
              The Comal River holds the title of one of the shortest navigable rivers in the world,
              stretching just over two miles from its headwaters at Comal Springs to its confluence
              with the Guadalupe River.
            </p>
            <pre className="bg-charcoal/5 text-charcoal/80 rounded-lg p-4 overflow-x-auto text-sm">
{`[Landa Park Springs] → [Wurstfest Grounds] → [The Tube Chute] → [Confluence with Guadalupe]`}
            </pre>
            <p>
              Because it is entirely spring-fed, the water remains a constant, refreshing 72°F (22°C)
              year-round. This makes it incredibly reliable, even during hot, dry Texas summers when
              other rivers experience low flows. However, because it is short, accessible, and located
              entirely within the city limits of New Braunfels, it operates much like a busy public
              park.
            </p>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Comal River Rules: The "Can Ban" and Life Jackets
            </h3>

            <Alert className="my-5 border-coral/40 bg-coral/5">
              <AlertTriangle className="h-4 w-4 text-coral" />
              <AlertTitle>Local "Can Ban" is strictly enforced</AlertTitle>
              <AlertDescription className="text-charcoal/80">
                The City of New Braunfels regulates recreation on the Comal River to manage trash and
                ensure public safety. Plan ahead so your day doesn't get derailed at the put-in.
              </AlertDescription>
            </Alert>

            <ul>
              <li>
                <strong>No disposable containers.</strong> No cans, plastic bottles, glass, zip-top
                bags, or paper wrappers on the water. Use reusable insulated tumblers or heavy-duty
                sports bottles.
              </li>
              <li>
                <strong>Cooler size limits.</strong> Coolers must not exceed 30 quarts, are limited
                to one per floater, and must have a locking mechanism or strap.
              </li>
              <li>
                <strong>Life jackets.</strong> Not legally mandated for adults in calm water but
                strongly recommended. <strong>Required by law for children under 13.</strong> Free
                loaner stations are available at major access points on a first-come basis.
              </li>
            </ul>

            <h3 className="font-heading text-2xl font-bold text-charcoal mt-8 mb-3">
              Navigating the Tube Chute and City Parking Realities
            </h3>
            <p>
              The defining feature of the Comal River float is the City Tube Chute — a concrete
              slide built into a dam that shoots tubers through a rush of whitewater. Thrilling for
              teens and adults, the chute can be intense: the exit pool is deep, turbulent, and
              frequently flips tubers out of their rings. Parents with young children often exit the
              river before the chute, walk around it, and re-enter downstream.
            </p>
            <p>
              Logistically, a public Comal float requires navigating city parking and shuttle
              schedules. On peak summer weekends, city-operated parking lots near Prince Solms Park
              charge premium rates. Once your float ends, you'll wait in shuttle lines with hundreds
              of other visitors to get back to your vehicle, making a quick, low-stress exit hard to
              coordinate. For an alternative,{" "}
              <Link to="/blog/tubing-guadalupe-river-new-braunfels" className="text-teal underline">
                tubing the Guadalupe River
              </Link>{" "}
              from a private outpost skips the public-park bottlenecks entirely.
            </p>

            {/* Comparison table */}
            <h2 id="comparison" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Direct Comparison: Comal River vs. Guadalupe River
            </h2>
            <p>
              Choosing between these two rivers depends on what kind of environment you want for
              your family or group. Here's how they compare side-by-side:
            </p>

            <div className="overflow-x-auto my-6 not-prose">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-teal/10 text-charcoal">
                    <th className="border border-border p-3 text-left">Feature</th>
                    <th className="border border-border p-3 text-left">Comal River</th>
                    <th className="border border-border p-3 text-left">Guadalupe River</th>
                  </tr>
                </thead>
                <tbody className="text-charcoal/85">
                  <tr>
                    <td className="border border-border p-3 font-semibold">Water source</td>
                    <td className="border border-border p-3">100% spring-fed (Comal Springs)</td>
                    <td className="border border-border p-3">Canyon Lake Dam releases + springs</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-semibold">Water temperature</td>
                    <td className="border border-border p-3">Constant 72°F (22°C) year-round</td>
                    <td className="border border-border p-3">Variable (usually 60s to mid-70s)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-semibold">Scenery</td>
                    <td className="border border-border p-3">Urban parks, residential backyards, concrete walls</td>
                    <td className="border border-border p-3">Cypress trees, limestone cliffs, Hill Country</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-semibold">Average float time</td>
                    <td className="border border-border p-3">Short (2–3 hours)</td>
                    <td className="border border-border p-3">Flexible (1 to 6+ hours)</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3 font-semibold">Logistical vibe</td>
                    <td className="border border-border p-3">Highly managed public park; concrete launches</td>
                    <td className="border border-border p-3">Natural riverbed; public parks + private outposts</td>
                  </tr>
                  <tr className="bg-muted/30">
                    <td className="border border-border p-3 font-semibold">Crowd levels</td>
                    <td className="border border-border p-3">Consistently high; weekend bottlenecks</td>
                    <td className="border border-border p-3">Variable; private stretches stay quiet</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <img
              src={inlineImage}
              alt="Friends floating the quiet Guadalupe River past cypress trees near Son's Guadalupe."
              width={1600}
              height={900}
              loading="lazy"
              decoding="async"
              className="w-full h-auto rounded-2xl shadow-soft my-8"
            />


            {/* Outfitters */}
            <h2 id="outfitters" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              How Public Outfitters Shape Your Trip: Navigating Local Providers
            </h2>
            <p>
              If you choose to navigate the public sections of either river, you'll likely work with
              one of the local outfitters that manage equipment rentals and shuttle logistics:
            </p>
            <ul>
              <li>
                <strong>Texas Tubes (Comal River).</strong> A classic choice for visitors looking for
                the standard, high-energy public float. Basic tube rentals and shuttles, operating
                entirely within the busy public flow.
              </li>
              <li>
                <strong>Comal Tubes (Comal River).</strong> Another standard public outfitter near
                the main launch points, with traditional tube packages and shuttle-back service.
              </li>
              <li>
                <strong>The Float In (Comal River).</strong> A commercialized riverside venue with
                seating, food, and private cabanas. More amenities than a public park, but still
                subject to the dense crowds of the public Comal corridor.
              </li>
              <li>
                <strong>Mountain Breeze Campground (Guadalupe River).</strong> A historic, high-energy
                campground with a lively, festival-like atmosphere and live music. Popular for young
                adult groups but often overwhelming for families seeking a quiet day.
              </li>
            </ul>

            {/* Son's Guadalupe */}
            <h2 id="sons-guadalupe" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Son's Guadalupe: The Crowd-Free, Private Cabana Alternative
            </h2>
            <p>
              For families, multi-generational groups, and planners who want to avoid chaotic public
              launches, city parking, and packed riverbanks,{" "}
              <Link to="/why-sons" className="text-teal underline font-semibold">Son's Guadalupe</Link>{" "}
              offers a premium, private-property alternative on the Guadalupe River. Located on a
              scenic stretch of{" "}
              <Link to="/blog/river-road-new-braunfels-guide" className="text-teal underline">
                River Road in New Braunfels
              </Link>
              , Son's bypasses the stress of public tubing with a curated, limited-capacity
              waterfront experience:
            </p>
            <pre className="bg-charcoal/5 text-charcoal/80 rounded-lg p-4 overflow-x-auto text-sm">
{`[Arrive & Park Onsite] → [Relax in Private Cabana] → [Shuttle-Assisted Float] → [Walk Back to Your Cabana]`}
            </pre>

            <div className="not-prose rounded-2xl border border-teal/30 bg-teal/5 p-6 my-6">
              <p className="font-heading text-xl font-bold text-charcoal mb-4">
                What you get at Son's Guadalupe
              </p>
              <ul className="space-y-3 text-charcoal/85">
                <li className="flex gap-3">
                  <Sun className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Guaranteed shaded retreats.</strong> Reserve a private cabana with
                    comfortable seating, BBQ grill, and electricity — no fighting for grass at a
                    public park.
                  </span>
                </li>
                <li className="flex gap-3">
                  <MapPin className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Stress-free water access.</strong> Lounge at in-water covered picnic
                    tables, swim, or take a 2–3 hour shuttle-assisted float that ends right back at
                    your cabana.
                  </span>
                </li>
                <li className="flex gap-3">
                  <Users className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Onsite amenities.</strong> Clean restrooms, private hot showers,
                    dedicated parking, and a daily guest cap that keeps the property from ever
                    feeling overcrowded.
                  </span>
                </li>
              </ul>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/cabanas">
                  <Button variant="hero">View Cabanas</Button>
                </Link>
                <Link to="/glamping">
                  <Button variant="outline">View Glamping</Button>
                </Link>
                <Link to="/tubing">
                  <Button variant="outline">Tubing Info</Button>
                </Link>
              </div>
            </div>

            {/* Checklist */}
            <h2 id="checklist" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Summary Checklist: Selecting Your Ideal New Braunfels River Trip
            </h2>

            <div className="grid md:grid-cols-2 gap-6 not-prose my-6">
              <div className="rounded-xl border border-border bg-card p-5">
                <p className="font-heading text-lg font-bold text-charcoal mb-3">
                  Choose the Comal River if…
                </p>
                <ul className="space-y-2 text-sm text-charcoal/85 list-disc pl-5">
                  <li>You want a short, quick float (under 3 hours).</li>
                  <li>You want guaranteed, consistent water levels regardless of recent rainfall.</li>
                  <li>Your group wants the thrill of the municipal Tube Chute.</li>
                  <li>You don't mind paid city parking, shuttle lines, and weekend crowds.</li>
                  <li>You're ready to pack drinks exclusively in reusable containers.</li>
                </ul>
              </div>
              <div className="rounded-xl border-2 border-teal/40 bg-teal/5 p-5">
                <p className="font-heading text-lg font-bold text-charcoal mb-3">
                  Choose the Guadalupe River (via Son's Guadalupe) if…
                </p>
                <ul className="space-y-2 text-sm text-charcoal/85 list-disc pl-5">
                  <li>You want a relaxed, family-friendly day away from rowdy crowds.</li>
                  <li>You want a dedicated home base with shade, seating, electricity, and a private grill.</li>
                  <li>You prefer a natural landscape of limestone cliffs and cypress trees.</li>
                  <li>You want simple parking and clean private restrooms right by the water.</li>
                  <li>You want the option to stay overnight in air-conditioned glamping tents.</li>
                </ul>
                <Link to="/book-now" className="inline-block mt-4">
                  <Button variant="hero" size="sm">Book your cabana</Button>
                </Link>
              </div>
            </div>

            {/* FAQs — Comal */}
            <h2 id="faqs-comal" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Frequently Asked Questions about the Comal River
            </h2>
            <div className="not-prose">
              <Accordion type="single" collapsible className="w-full">
                {FAQS.map((f, i) => (
                  <AccordionItem key={`comal-${i}`} value={`faq-comal-${i}`}>
                    <AccordionTrigger className="text-left font-heading font-semibold text-charcoal">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-charcoal/85 leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* FAQs — Guadalupe */}
            <h2 id="faqs-guadalupe" className="font-heading text-3xl font-bold text-charcoal mt-12 mb-4">
              Frequently Asked Questions about the Guadalupe River
            </h2>
            <div className="not-prose">
              <Accordion type="single" collapsible className="w-full">
                {FAQS_GUADALUPE.map((f, i) => (
                  <AccordionItem key={`guad-${i}`} value={`faq-guad-${i}`}>
                    <AccordionTrigger className="text-left font-heading font-semibold text-charcoal">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-charcoal/85 leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>


          {/* Final CTA */}
          <section className="mt-14 rounded-2xl bg-charcoal text-white p-8 md:p-10 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">
              Skip the crowds. Float the Guadalupe with us.
            </h2>
            <p className="text-white/85 max-w-2xl mx-auto mb-6">
              Private cabanas, onsite parking, and a shuttle-assisted float that ends at your door —
              a calmer, family-first way to spend a Texas summer day.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/book-now">
                <Button variant="hero" size="lg">Book your cabana</Button>
              </Link>
              <Link to="/why-sons">
                <Button variant="outline" size="lg" className="bg-transparent text-white border-white hover:bg-white hover:text-charcoal">
                  Why Son's Guadalupe
                </Button>
              </Link>
            </div>
          </section>
        </article>

        <RelatedReads posts={related} />
      </main>

      <Footer />
    </div>
  );
};

export default BlogComalVsGuadalupe;
